// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import {
  ENTRA_SIGN_IN_TEST_HINTS,
  OAUTH_SIGN_IN_TEST_HINTS,
  SIGN_IN_TEST_REDIRECT_TOKEN,
  signInTestHintFor,
  type IdpTestSignInOutcome,
  type IdpTestSignInPurpose,
  type SignInTestHint,
} from '@greenlight/shared';

import type { IdpProviderId } from '@/lib/idpProviders';

/** The wizard's test sign-in runs the real login with its purpose sealed server-side. */
export function idpTestSignInHref(
  greenlightBaseUrl: string,
  purpose: IdpTestSignInPurpose,
): string {
  return `${greenlightBaseUrl.replace(/\/$/, '')}/auth/login?install_test=${purpose}`;
}

export interface IdpTestComparisonRow {
  label: string;
  value: string;
}

export interface IdpTestFailureView {
  title: string;
  detail: string;
  /** The value the token carried beside the value the wizard saved. */
  comparison: IdpTestComparisonRow[];
  fix?: string;
}

interface FailureContext {
  purpose: IdpTestSignInPurpose;
  provider: IdpProviderId | undefined;
  groupClaim: string;
  redirectUri: string;
}

const PROVIDER_CLAIM_FIX: Record<IdpProviderId, string> = {
  entra:
    'In Entra, open the app registration → Token configuration → Add groups claim and choose "Groups assigned to the application", then assign the mapped groups to the enterprise application. Or reconfigure with Greenlight-managed roles.',
  okta: 'On the Okta authorization server, add a groups claim to the ID token under the name the wizard expects. Or reconfigure with Greenlight-managed roles.',
  google:
    'Google issues no groups claim; roles are managed in Greenlight. Switch to Greenlight-managed roles in the form above and save.',
  oidc: 'Configure your provider to emit group memberships in the ID token under that claim name, or reconfigure with Greenlight-managed roles.',
};

function list(values: readonly string[] | undefined): string {
  return values && values.length > 0 ? values.join(', ') : '(none)';
}

function withRedirectUri(text: string, redirectUri: string): string {
  return text.split(SIGN_IN_TEST_REDIRECT_TOKEN).join(redirectUri);
}

/** Plain words for a recorded failure: what was compared, what did not match, and the fix. */
export function describeIdpTestFailure(
  outcome: IdpTestSignInOutcome,
  ctx: FailureContext,
): IdpTestFailureView {
  const expected = outcome.expected ?? {};
  const who = outcome.email ? `You signed in as ${outcome.email}.` : 'The sign-in came back.';
  const groupRows = (): IdpTestComparisonRow[] => [
    { label: `Groups in the token (${ctx.groupClaim})`, value: list(outcome.groups) },
    ...(expected.org_admin_groups
      ? [{ label: 'Org admin group', value: list(expected.org_admin_groups) }]
      : []),
    ...(expected.user_groups
      ? [{ label: 'User sign-in group', value: list(expected.user_groups) }]
      : []),
  ];

  if (outcome.provider_error) {
    const hints = ctx.provider === 'entra' ? ENTRA_SIGN_IN_TEST_HINTS : OAUTH_SIGN_IN_TEST_HINTS;
    const hint = signInTestHintFor(hints, outcome.code);
    return {
      title: 'Your identity provider refused the sign-in',
      detail:
        hint?.cause ??
        `The provider answered ${outcome.provider_error.error}${
          outcome.provider_error.description ? `: ${outcome.provider_error.description}` : '.'
        }`,
      comparison: hint
        ? [{ label: 'Provider error', value: outcome.code ?? outcome.provider_error.error }]
        : [],
      ...(hint ? { fix: withRedirectUri(hint.fix, ctx.redirectUri) } : {}),
    };
  }

  switch (outcome.code) {
    case 'auth.test_role_below_org_admin':
      if (expected.admin_email) {
        return {
          title: 'This account is not the saved org admin',
          detail: `${who} The org admin email saved on this step is different, so this account would sign in as a ${outcome.role ?? 'user'}, not an org admin.`,
          comparison: [
            { label: 'Signed in as', value: outcome.email ?? '(unknown)' },
            { label: 'Saved org admin email', value: expected.admin_email },
          ],
          fix: 'If the saved email has a typo, correct it in the form above and save. If the email is right, test again signed in as that account.',
        };
      }
      return {
        title: 'This account would not be an org admin',
        detail: `${who} None of the groups in the ID token is mapped to the org admin role, so this account would sign in as a ${outcome.role ?? 'user'}.`,
        comparison: groupRows(),
        fix: 'Compare the two lists: the org admin group must appear in the token exactly as saved. Fix the identifier in the form above and save, or add this account to the group and test again.',
      };
    case 'auth.group_claim_missing':
      return {
        title: `The ID token carried no ${ctx.groupClaim} claim`,
        detail: `${who} IdP-managed roles need group memberships in the token, and this provider sent none under that claim name.`,
        comparison: groupRows(),
        fix: PROVIDER_CLAIM_FIX[ctx.provider ?? 'oidc'],
      };
    case 'auth.forbidden':
      return {
        title: 'The user sign-in group refused this account',
        detail: `${who} A user sign-in group is set, and none of the groups in the token matches it.`,
        comparison: groupRows(),
        fix: 'Add this account to the user sign-in group, correct the group identifier in the form above and save, or clear that field to let anyone your provider admits sign in.',
      };
    case 'auth.user_inactive':
      return {
        title: 'This account is deactivated in Greenlight',
        detail: `${who} The account exists but was deactivated, so it cannot sign in.`,
        comparison: [],
        fix: 'Test again with an active account, or name a different org admin in the form above and save.',
      };
    case 'auth.hosted_domain_mismatch':
      return {
        title: 'This account is outside the Workspace domains',
        detail: `${who} Google reported a domain that is not in the saved list, so sign-in is refused.`,
        comparison: [
          { label: 'Account domain', value: outcome.hosted_domain ?? '(none reported)' },
          { label: 'Workspace domains', value: list(expected.hosted_domains) },
        ],
        fix: 'Add the domain in the form above and save, or test again with an account in a listed domain.',
      };
    case 'auth.email_unverified':
      return {
        title: 'Google reports this email as unverified',
        detail: 'Greenlight only admits verified Google accounts.',
        comparison: [],
        fix: 'Verify the account in Google Workspace, or test with a different account.',
      };
    case 'auth.email_missing':
      return {
        title: 'The ID token carried no email',
        detail:
          'Greenlight matches people by email and this token had neither an email nor a preferred username.',
        comparison: [],
        fix: 'Allow the email (and profile) scope on the client, or add an email claim to the ID token.',
      };
    case 'auth.token_exchange_failed':
      return {
        title: 'The provider refused to redeem the sign-in',
        detail:
          'The authorization code came back, but the token request was refused. This is usually a wrong or expired client secret.',
        comparison: [],
        fix: 'Create a new client secret in the provider and save it in the form above.',
      };
    default:
      return {
        title: ctx.purpose === 'admin' ? 'The test sign-in failed' : 'The user test sign-in failed',
        detail: `${who} The sign-in was refused with ${outcome.code ?? 'an unknown error'}.`,
        comparison: [],
      };
  }
}

const NO_RETURN_ENTRA_CODES = ['AADSTS500113', 'AADSTS50011', 'AADSTS700016', 'AADSTS50105'];

/**
 * What usually went wrong when the provider showed an error page instead of
 * redirecting back, with the install's own callback in place of the token.
 */
export function idpTestNoReturnHints(
  provider: IdpProviderId | undefined,
  redirectUri: string,
): SignInTestHint[] {
  const hints =
    provider === 'entra'
      ? ENTRA_SIGN_IN_TEST_HINTS.filter(
          (h) => h.code !== null && NO_RETURN_ENTRA_CODES.includes(h.code),
        )
      : OAUTH_SIGN_IN_TEST_HINTS.filter((h) => h.code === null);
  return hints.map((h) => ({
    ...h,
    cause: withRedirectUri(h.cause, redirectUri),
    fix: withRedirectUri(h.fix, redirectUri),
  }));
}
